import type { FC } from "react";
import { useEffect, useState } from "react";

import clsx from "clsx";

import PopularCashbackBrands, {
    type IPopularCashbackBrands,
} from "@/src/cashback/LandingPage/PopularCashbackBrands";
import WhatIsCashback from "@/src/cashback/LandingPage/WhatIsCashback";
import { languageSelector, useViewportInfoContext } from "@/src/contexts";
import LoginResources from "@/src/modals/login-registration/Login.Resources";
import LoginResourcesEn from "@/src/modals/login-registration/Login.Resources.En";
import RegistrationResources from "@/src/modals/login-registration/Registration.Resources";
import RegistrationResourcesEn from "@/src/modals/login-registration/Registration.Resources.En";
import { ConsentScreenType } from "@/src/modals/useConsents";
import PageLayout from "@/src/shared/page-layout";
import PanelSection from "@/src/shared/panel-section";
import useAjax from "@Components/Hooks/useAjax";
import ImageLoader from "@Components/ImageLoader";
import { MgmEventType, getCdnImage } from "@Components/Modules/Helpers";
import { LoginRegistrationCore } from "@Components/login-registration-core/login-registration-core";
import { allStoresCategoriesPanelId } from "@Constants/PortalConstants";
import { Default } from "@Routes/MemberGetMemberLinks";

import * as portalCss from "../../shared/Portal.module.css";
import * as css from "./MgmLoginAndRegistrationPage.module.css";

export type IMgmLoginAndRegistrationPage = {
    code: string;
    popularCashbackBrands: IPopularCashbackBrands;
    receiverBonusAmount: number;
};

const MgmLoginAndRegistrationPage: FC<IMgmLoginAndRegistrationPage> = ({
    code,
    popularCashbackBrands,
    receiverBonusAmount,
}) => {
    const [isRegistration, setIsRegistration] = useState(true);

    const { isMobile } = useViewportInfoContext();
    const { data: popularCashbackBrandsResponse, request: popularCashbackBrandsRequest } = useAjax({
        ajaxAction: Default.url,
        initialData: popularCashbackBrands,
    });

    const resources = languageSelector(
        isRegistration ? RegistrationResources : LoginResources,
        isRegistration ? RegistrationResourcesEn : LoginResourcesEn,
    );

    useEffect(() => {
        if (!isMobile && !popularCashbackBrandsResponse) {
            popularCashbackBrandsRequest({ action: "GetPopularCashbackBrands" });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMobile]);

    return (
        <PageLayout isNewGrid breadcrumbs={[{ labelKey: "mgm" }]}>
            <div className={`${css.loginAndRegistrationPageComponent} ${portalCss.standardContainerV2}`}>
                <section className={clsx(css.container, portalCss.setSectionColOne)}>
                    <div className={css.title}>
                        <h1 dangerouslySetInnerHTML={{ __html: resources.title }} />
                        <h2
                            dangerouslySetInnerHTML={{
                                __html: resources.description.replace(
                                    "{receiverBonusAmount}",
                                    `${receiverBonusAmount} zł`,
                                ),
                            }}
                        />
                        <h5>
                            <span
                                className={css.codeDescription}
                                dangerouslySetInnerHTML={{ __html: resources.codeDescription }}
                            />
                            <span>{code}</span>
                        </h5>
                    </div>

                    <ImageLoader src={getCdnImage(process.env.MGM_MainImg)} alt="Logo" />

                    <LoginRegistrationCore
                        loginConsentScreenType={ConsentScreenType.LoginProcess}
                        registerConsentScreenType={ConsentScreenType.MgmRegistrationProcess}
                        getIsRegistration={setIsRegistration}
                        viewTelemetry={{
                            param1: MgmEventType.loginAndRegisterParam,
                        }}
                    />
                </section>

                <PanelSection {...resources.whatIsCashback}>
                    <WhatIsCashback />
                </PanelSection>
                {!isMobile && (
                    <PanelSection {...resources.popularCashbackBrands} id={allStoresCategoriesPanelId}>
                        <PopularCashbackBrands {...popularCashbackBrands} />
                    </PanelSection>
                )}
            </div>
        </PageLayout>
    );
};

export { MgmLoginAndRegistrationPage };
